import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import axios from "../axiosConfig";
import RfpReviewFilterRow from "./rfp/RfpReviewFilterRow";
import RfpPdfPanel from "./rfp/RfpPdfPanel";
import RfpStickySaveBar from "./rfp/RfpStickySaveBar";
import RfpComplianceChip, { fallbackDisagreementLabel } from "./rfp/RfpComplianceChip";
import RfpProductDecisionDialog from "./rfp/RfpProductDecisionDialog";
import {
  decisionItemTitle,
  decisionLabel,
  findDecisionItem,
  openItemsCoveringRow,
  resolvedDecisionForRow,
  rowOverlapsItem,
} from "./rfp/productDecision";
import {
  CLASSES,
  classLabel,
  classSelectClass,
  groupRowsBySection,
  isRequirementFromClass,
  normalizeGridClass,
  productChipClass,
  excludedFocusRowIds,
  tocRowIdsToHide,
} from "./rfp/rfpUtils";

const API = "/api";
const ROW_DRAG_TYPE = "application/x-rfp-row-id";
const HISTORY_LIMIT = 50;
const AUTOSAVE_MS = 2000;

function solutionLabel(value) {
  return {
    cloud_appsec: "Cloud AppSec",
    integrated_waf: "Integrated WAF",
    alteon: "Alteon",
  }[value] || value || "";
}

function areSelectedAdjacent(selectedIds, rows) {
  if (selectedIds.length < 2) return false;
  const indices = selectedIds
    .map((id) => rows.findIndex((r) => r.id === id))
    .filter((i) => i >= 0)
    .sort((a, b) => a - b);
  if (indices.length !== selectedIds.length) return false;
  for (let i = 1; i < indices.length; i += 1) {
    if (indices[i] !== indices[i - 1] + 1) return false;
  }
  return true;
}

function TrashIcon() {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" className="h-4 w-4" aria-hidden>
      <path
        fillRule="evenodd"
        d="M8.75 1A2.75 2.75 0 006 3.75v.443c-.795.077-1.584.176-2.365.298a.75.75 0 10.23 1.482l.149-.022.841 9.518A2.75 2.75 0 007.596 19h4.807a2.75 2.75 0 002.742-2.53l.841-9.519.149.023a.75.75 0 00.23-1.482A41.03 41.03 0 0014 4.193V3.75A2.75 2.75 0 0011.25 1h-2.5zM10 4c.84 0 1.673.025 2.5.075V3.75c0-.69-.56-1.25-1.25-1.25h-2.5c-.69 0-1.25.56-1.25 1.25v.325C8.327 4.025 9.16 4 10 4zM8.58 7.72a.75.75 0 00-1.5.06l.3 7.5a.75.75 0 101.5-.06l-.3-7.5zm4.34.06a.75.75 0 10-1.5-.06l-.3 7.5a.75.75 0 101.5.06l.3-7.5z"
        clipRule="evenodd"
      />
    </svg>
  );
}

function normalizeLoadedRows(data) {
  return (data || []).map((row, idx) => ({
    ...row,
    se_comment: (row.se_comment || "").trim(),
    class: normalizeGridClass(row.class),
    sequence: idx + 1,
  }));
}

function fieldChanged(row, orig, field) {
  if (field === "se_comment") {
    return (row.se_comment || "").trim() !== (orig.se_comment || "").trim();
  }
  if (field === "class") {
    return normalizeGridClass(row.class) !== normalizeGridClass(orig.class);
  }
  if (field === "is_requirement") {
    return Boolean(row.is_requirement) !== Boolean(orig.is_requirement);
  }
  return row[field] !== orig[field];
}

function resequenceRows(rows) {
  return rows.map((row, idx) => ({ ...row, sequence: idx + 1 }));
}

function moveRowRelative(rows, sourceId, targetId, insertAfter) {
  if (!sourceId || !targetId || sourceId === targetId) return rows;
  const from = rows.findIndex((r) => r.id === sourceId);
  const to = rows.findIndex((r) => r.id === targetId);
  if (from < 0 || to < 0) return rows;
  const copy = [...rows];
  const [item] = copy.splice(from, 1);
  let insertAt = copy.findIndex((r) => r.id === targetId);
  if (insertAt < 0) return rows;
  if (insertAfter) insertAt += 1;
  copy.splice(insertAt, 0, item);
  return resequenceRows(copy);
}

function insertRowAfter(rows, anchorId, newRow) {
  const idx = rows.findIndex((r) => r.id === anchorId);
  const insertAt = idx >= 0 ? idx + 1 : rows.length;
  return resequenceRows([...rows.slice(0, insertAt), newRow, ...rows.slice(insertAt)]);
}

function cloneGridSnapshot(rows, selectedIds) {
  return {
    rows: JSON.parse(JSON.stringify(rows)),
    selected: [...selectedIds],
  };
}

function countRowDiffs(rows, baseline) {
  if (!baseline?.length) return 0;
  const baseMap = new Map(baseline.map((r) => [r.id, r]));
  let n = 0;
  for (const row of rows) {
    const orig = baseMap.get(row.id);
    if (!orig) {
      n += 1;
      continue;
    }
    if (JSON.stringify(row) !== JSON.stringify(orig)) n += 1;
  }
  for (const b of baseline) {
    if (!rows.find((r) => r.id === b.id)) n += 1;
  }
  return n;
}

export default function RfpRequirementGrid({
  jobId,
  onSaved,
  onToast,
  onDirtyChange,
  onShowShortcuts,
  scrollToRowId,
  highlightRowId,
  onHighlightDone,
  complianceById,
  onOpenCompliance,
  sourceFormat = "pdf",
  scopeResult = null,
  decisionFocusId = null,
  productDecisionFocus = 0,
  onClearDecisionFocus,
  onProductDecisionApplied,
  onOpenProductRowCount,
}) {
  const [rows, setRows] = useState([]);
  const [baseline, setBaseline] = useState([]);
  const [technologies, setTechnologies] = useState([]);
  const [classificationSource, setClassificationSource] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [autosaving, setAutosaving] = useState(false);
  const [error, setError] = useState(null);
  const [selected, setSelected] = useState(() => new Set());
  const [panelRow, setPanelRow] = useState(null);
  const [search, setSearch] = useState("");
  const [techFilter, setTechFilter] = useState("");
  const [classFilter, setClassFilter] = useState("");
  const [reviewFilter, setReviewFilter] = useState("");
  const [decisionDialog, setDecisionDialog] = useState(null);
  const [showToc, setShowToc] = useState(false);
  const [showExcluded, setShowExcluded] = useState(false);
  const [bulkTech, setBulkTech] = useState("");
  const [dragId, setDragId] = useState(null);
  const [dropHint, setDropHint] = useState(null);
  const [panelCollapsed, setPanelCollapsed] = useState(false);
  const [collapsedSections, setCollapsedSections] = useState(() => new Set());
  const lastClicked = useRef(null);
  const scrollContainerRef = useRef(null);
  const pendingScrollRestore = useRef(null);
  const selectedRef = useRef(selected);
  const undoStackRef = useRef([]);
  const redoStackRef = useRef([]);
  const skipHistoryRef = useRef(false);
  const autosaveTimerRef = useRef(null);
  const pendingCommentSaveRef = useRef(null);
  const [canUndo, setCanUndo] = useState(false);
  const [canRedo, setCanRedo] = useState(false);
  const [flashRowId, setFlashRowId] = useState(null);

  selectedRef.current = selected;

  useEffect(() => {
    if (!scrollToRowId || loading) return undefined;
    const row = rows.find((r) => r.id === scrollToRowId);
    if (!row) return undefined;
    const timer = setTimeout(() => {
      const el = document.querySelector(`[data-row-id="${scrollToRowId}"]`);
      el?.scrollIntoView({ behavior: "smooth", block: "center" });
      setFlashRowId(scrollToRowId);
      setPanelRow(row);
      onHighlightDone?.();
    }, 100);
    const clearTimer = setTimeout(() => setFlashRowId(null), 3500);
    return () => {
      clearTimeout(timer);
      clearTimeout(clearTimer);
    };
  }, [scrollToRowId, loading, rows, onHighlightDone]);

  useEffect(() => {
    if (!highlightRowId) return undefined;
    setFlashRowId(highlightRowId);
    const timer = setTimeout(() => setFlashRowId(null), 3500);
    return () => clearTimeout(timer);
  }, [highlightRowId]);

  const syncHistoryFlags = useCallback(() => {
    setCanUndo(undoStackRef.current.length > 0);
    setCanRedo(redoStackRef.current.length > 0);
  }, []);

  const clearHistory = useCallback(() => {
    undoStackRef.current = [];
    redoStackRef.current = [];
    syncHistoryFlags();
  }, [syncHistoryFlags]);

  const pushHistory = useCallback(
    (rowsSnap, selectedSnap) => {
      if (skipHistoryRef.current) return;
      undoStackRef.current.push(cloneGridSnapshot(rowsSnap, selectedSnap));
      if (undoStackRef.current.length > HISTORY_LIMIT) undoStackRef.current.shift();
      redoStackRef.current = [];
      syncHistoryFlags();
    },
    [syncHistoryFlags]
  );

  const restoreSnapshot = useCallback(
    (snap) => {
      skipHistoryRef.current = true;
      setRows(JSON.parse(JSON.stringify(snap.rows)));
      setSelected(new Set(snap.selected));
      skipHistoryRef.current = false;
      syncHistoryFlags();
    },
    [syncHistoryFlags]
  );

  const undo = useCallback(() => {
    const stack = undoStackRef.current;
    if (!stack.length) return;
    redoStackRef.current.push(cloneGridSnapshot(rows, selectedRef.current));
    restoreSnapshot(stack.pop());
    onToast?.info("Undone.");
  }, [rows, restoreSnapshot, onToast]);

  const redo = useCallback(() => {
    const stack = redoStackRef.current;
    if (!stack.length) return;
    undoStackRef.current.push(cloneGridSnapshot(rows, selectedRef.current));
    restoreSnapshot(stack.pop());
    onToast?.info("Redone.");
  }, [rows, restoreSnapshot, onToast]);

  const setRowsWithHistory = useCallback(
    (updater) => {
      setRows((prev) => {
        const next = typeof updater === "function" ? updater(prev) : updater;
        if (!skipHistoryRef.current && JSON.stringify(next) !== JSON.stringify(prev)) {
          pushHistory(prev, selectedRef.current);
        }
        return next;
      });
    },
    [pushHistory]
  );

  const commitRows = useCallback(
    (data, technologiesList, { preserveSelection = false, resetHistory = true } = {}) => {
      const ordered = normalizeLoadedRows(data);
      skipHistoryRef.current = true;
      setTechnologies(technologiesList || []);
      setRows(ordered);
      setBaseline(JSON.parse(JSON.stringify(ordered)));
      if (!preserveSelection) setSelected(new Set());
      skipHistoryRef.current = false;
      if (resetHistory) clearHistory();
      return ordered;
    },
    [clearHistory]
  );

  useLayoutEffect(() => {
    const pending = pendingScrollRestore.current;
    if (!pending) return;
    pendingScrollRestore.current = null;
    window.scrollTo(0, pending.windowY);
    if (scrollContainerRef.current) scrollContainerRef.current.scrollTop = pending.containerY;
  }, [rows]);

  const load = useCallback(
    async ({ silent = false, preserveSelection = false } = {}) => {
      if (!jobId) return;
      if (!silent) setLoading(true);
      setError(null);
      try {
        const res = await axios.get(`${API}/rfp-jobs/${jobId}/requirements`);
        commitRows(res.data?.requirements || [], res.data?.technologies || [], { preserveSelection });
        setClassificationSource(res.data?.active_grid_label_source || res.data?.grid_label_source?.active_grid_label_source || null);
      } catch (err) {
        setError(err?.response?.data?.detail || err.message || "Failed to load requirements");
      } finally {
        if (!silent) setLoading(false);
      }
    },
    [jobId, commitRows]
  );

  useEffect(() => {
    load();
  }, [load]);

  const dirty = useMemo(() => {
    if (JSON.stringify(rows) !== JSON.stringify(baseline)) return true;
    return rows.some((row) => Boolean(row.is_requirement) !== isRequirementFromClass(row.class));
  }, [rows, baseline]);
  const dirtyCount = useMemo(() => {
    const diffs = countRowDiffs(rows, baseline);
    const extra = rows.filter(
      (row) => Boolean(row.is_requirement) !== isRequirementFromClass(row.class)
    ).length;
    return diffs + extra;
  }, [rows, baseline]);

  useEffect(() => {
    onDirtyChange?.({ dirty, dirtyCount });
  }, [dirty, dirtyCount, onDirtyChange]);

  useEffect(() => {
    const onBeforeUnload = (e) => {
      if (!dirty) return;
      e.preventDefault();
      e.returnValue = "";
    };
    window.addEventListener("beforeunload", onBeforeUnload);
    return () => window.removeEventListener("beforeunload", onBeforeUnload);
  }, [dirty]);

  useEffect(() => {
    const onKeyDown = (e) => {
      if (e.key === "?" && !e.ctrlKey && !e.metaKey) {
        const tag = e.target?.tagName;
        if (tag !== "TEXTAREA" && tag !== "INPUT") {
          e.preventDefault();
          onShowShortcuts?.();
        }
      }
      if (!(e.ctrlKey || e.metaKey)) return;
      const tag = e.target?.tagName;
      if (tag === "TEXTAREA" || tag === "INPUT" || tag === "SELECT") return;
      if (e.key === "z" && !e.shiftKey) {
        e.preventDefault();
        undo();
      } else if (e.key === "y" || (e.key === "z" && e.shiftKey)) {
        e.preventDefault();
        redo();
      }
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [undo, redo, onShowShortcuts]);

  const hiddenTocIds = useMemo(() => tocRowIdsToHide(rows), [rows]);
  const hiddenFocusIds = useMemo(() => excludedFocusRowIds(rows), [rows]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return rows.filter((row) => {
      if (!showToc && hiddenTocIds.has(row.id)) return false;
      if (!showExcluded && hiddenFocusIds.has(row.id)) return false;
      if (techFilter && row.technology !== techFilter) return false;
      if (classFilter && normalizeGridClass(row.class) !== classFilter) return false;
      if (reviewFilter === "has_comment" && !(row.se_comment || "").trim()) return false;
      if (reviewFilter === "not_relevant" && row.technology !== "Not relevant") return false;
      if (reviewFilter === "classification_review" && !row.review_required) return false;
      if (decisionFocusId) {
        const focusItem = findDecisionItem(scopeResult, decisionFocusId);
        if (!focusItem || !rowOverlapsItem(row, focusItem)) return false;
        if (row.technology !== "Gen Req" && row.scope_decision_item_id !== decisionFocusId) return false;
      } else if (reviewFilter === "product_decision" && !openItemsCoveringRow(row, scopeResult).length) {
        return false;
      }
      if (reviewFilter === "compliant" && complianceById?.[row.id]?.chip !== "Compliant") return false;
      if (reviewFilter === "partial" && complianceById?.[row.id]?.chip !== "Partially compliant") return false;
      if (reviewFilter === "non_compliant" && complianceById?.[row.id]?.chip !== "Non-compliant") return false;
      if (reviewFilter === "error" && complianceById?.[row.id]?.chip !== "Error") return false;
      if (q) {
        const hay = `${row.text || ""} ${row.id || ""} ${row.section_title || ""} ${row.solution_type || ""} ${(row.classification_warnings || []).join(" ")}`.toLowerCase();
        if (!hay.includes(q)) return false;
      }
      return true;
    });
  }, [rows, techFilter, classFilter, reviewFilter, search, complianceById, showToc, hiddenTocIds, showExcluded, hiddenFocusIds, decisionFocusId, scopeResult]);

  const sectionGroups = useMemo(() => groupRowsBySection(filtered), [filtered]);

  const selectedRowsInOrder = useMemo(() => rows.filter((r) => selected.has(r.id)), [rows, selected]);

  const canMergeSelected = useMemo(
    () => selected.size >= 2 && areSelectedAdjacent([...selected], rows),
    [selected, rows]
  );

  const persistSave = useCallback(
    async ({ silent = false, commentOnlyIds = null } = {}) => {
      if (!silent) setSaving(true);
      else setAutosaving(true);
      setError(null);
      if (!silent) {
        pendingScrollRestore.current = {
          windowY: window.scrollY,
          containerY: scrollContainerRef.current?.scrollTop ?? 0,
        };
      }
      try {
        const baseIds = new Set(baseline.map((r) => r.id));
        const curIds = new Set(rows.map((r) => r.id));
        const delete_ids = commentOnlyIds ? [] : [...baseIds].filter((id) => !curIds.has(id));
        const creates = [];
        const updates = [];
        for (const row of rows) {
          if (commentOnlyIds && !commentOnlyIds.includes(row.id)) continue;
          if (row._isNew && !commentOnlyIds) {
            creates.push({
              client_id: row.id,
              section_id: row.section_id,
              technology: row.technology,
              text: row.text,
              req_class: row.class,
              is_requirement: isRequirementFromClass(row.class),
              sequence: row.sequence,
              se_comment: row.se_comment || "",
            });
            continue;
          }
          const orig = baseline.find((b) => b.id === row.id);
          if (!orig) continue;
          const u = { id: row.id };
          let changed = false;
          const fields = commentOnlyIds
            ? [["se_comment", "se_comment"]]
            : [
                ["technology", "technology"],
                ["text", "text"],
                ["class", "req_class"],
                ["is_requirement", "is_requirement"],
                ["se_comment", "se_comment"],
              ];
          for (const [src, dst] of fields) {
            if (fieldChanged(row, orig, src)) {
              u[dst] = src === "se_comment" ? (row.se_comment || "").trim() : row[src];
              changed = true;
            }
          }
          if (!commentOnlyIds) {
            const derivedReq = isRequirementFromClass(row.class);
            if (Boolean(orig.is_requirement) !== derivedReq) {
              u.is_requirement = derivedReq;
              changed = true;
            }
          }
          if (changed) updates.push(u);
        }
        if (!updates.length && !creates.length && !delete_ids.length) return null;
        const payload = { updates, create_rows: creates, delete_ids };
        if (!commentOnlyIds) payload.row_order = rows.map((r) => r.id);
        const res = await axios.put(`${API}/rfp-jobs/${jobId}/requirements`, payload);
        const saved = res.data?.requirements || rows;
        commitRows(saved, technologies, { preserveSelection: true, resetHistory: !commentOnlyIds });
        if (!silent) {
          onToast?.success(
            `Saved (${res.data?.changed || 0} updated, ${res.data?.added || 0} added, ${delete_ids.length} deleted)`
          );
          onSaved?.(res.data);
        }
        return res.data;
      } catch (err) {
        if (!silent) pendingScrollRestore.current = null;
        const msg = err?.response?.data?.detail || err.message || "Save failed";
        setError(msg);
        if (!silent) onToast?.error(msg);
        return null;
      } finally {
        if (!silent) setSaving(false);
        else setAutosaving(false);
      }
    },
    [baseline, rows, jobId, technologies, commitRows, onSaved, onToast]
  );

  const scheduleCommentAutosave = useCallback(
    (rowId) => {
      pendingCommentSaveRef.current = rowId;
      if (autosaveTimerRef.current) clearTimeout(autosaveTimerRef.current);
      autosaveTimerRef.current = setTimeout(async () => {
        const ids = [];
        const baseMap = new Map(baseline.map((r) => [r.id, r]));
        for (const row of rows) {
          const orig = baseMap.get(row.id);
          if (orig && fieldChanged(row, orig, "se_comment")) ids.push(row.id);
        }
        if (ids.length) {
          await persistSave({ silent: true, commentOnlyIds: ids });
          onToast?.info("SE comments auto-saved.", 2500);
        }
      }, AUTOSAVE_MS);
    },
    [baseline, rows, persistSave, onToast]
  );

  useEffect(
    () => () => {
      if (autosaveTimerRef.current) clearTimeout(autosaveTimerRef.current);
    },
    []
  );

  const updateRow = (id, patch, { autosaveComment = false } = {}) => {
    setRowsWithHistory((prev) => prev.map((r) => (r.id === id ? { ...r, ...patch } : r)));
    if (autosaveComment && "se_comment" in patch) scheduleCommentAutosave(id);
  };

  const toggleSelect = (id, shiftKey) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (shiftKey && lastClicked.current) {
        const ids = filtered.map((r) => r.id);
        const a = ids.indexOf(lastClicked.current);
        const b = ids.indexOf(id);
        if (a >= 0 && b >= 0) {
          const [lo, hi] = a < b ? [a, b] : [b, a];
          for (let i = lo; i <= hi; i += 1) next.add(ids[i]);
          lastClicked.current = id;
          return next;
        }
      }
      if (next.has(id)) next.delete(id);
      else next.add(id);
      lastClicked.current = id;
      return next;
    });
    const row = rows.find((r) => r.id === id);
    if (row) {
      setPanelRow(row);
      setPanelCollapsed(false);
    }
  };

  const selectAllVisible = (checked) => {
    setSelected(checked ? new Set(filtered.map((r) => r.id)) : new Set());
  };

  const mergeSelected = () => {
    if (selectedRowsInOrder.length < 2) {
      onToast?.error("Select at least 2 rows to merge.");
      return;
    }
    if (!canMergeSelected) {
      onToast?.error("Select adjacent rows to merge.");
      return;
    }
    const combinedText = selectedRowsInOrder.map((r) => (r.text || "").trim()).filter(Boolean).join("\n\n");
    const combinedComment = selectedRowsInOrder.map((r) => (r.se_comment || "").trim()).filter(Boolean).join("\n\n");
    const kept = { ...selectedRowsInOrder[0], text: combinedText, se_comment: combinedComment };
    const removeIds = new Set(selectedRowsInOrder.slice(1).map((r) => r.id));
    setRowsWithHistory((prev) =>
      prev.filter((r) => !removeIds.has(r.id)).map((r) => (r.id === kept.id ? kept : r))
    );
    setSelected(new Set([kept.id]));
    onToast?.info("Rows merged — click Save to persist.");
  };

  const deleteSelected = () => {
    if (!selectedRowsInOrder.length) return;
    if (!window.confirm(`Delete ${selectedRowsInOrder.length} row(s)?`)) return;
    setRowsWithHistory((prev) => prev.filter((r) => !selected.has(r.id)));
    setSelected(new Set());
    if (panelRow && selected.has(panelRow.id)) setPanelRow(null);
    onToast?.info("Rows removed — click Save to persist.");
  };

  const deleteRow = (id, e) => {
    e?.stopPropagation();
    if (!window.confirm("Delete this row?")) return;
    setRowsWithHistory((prev) => prev.filter((r) => r.id !== id));
    setSelected((prev) => {
      const next = new Set(prev);
      next.delete(id);
      return next;
    });
    if (panelRow?.id === id) setPanelRow(null);
    onToast?.info("Row removed — click Save to persist.");
  };

  const markNotRelevant = () => {
    if (!selected.size) return;
    setRowsWithHistory((prev) =>
      prev.map((r) => (selected.has(r.id) ? { ...r, technology: "Not relevant" } : r))
    );
    onToast?.info(`Marked ${selected.size} row(s) as Not relevant.`);
  };

  const handleAddRow = () => {
    const anchorId = lastClicked.current || selectedRowsInOrder.at(-1)?.id || filtered.at(-1)?.id;
    const anchor = rows.find((r) => r.id === anchorId);
    const tempId = `new-${Date.now()}`;
    const newRow = {
      id: tempId,
      section_id: anchor?.section_id || "s1",
      section_title: anchor?.section_title || "Document",
      section_legacy: anchor?.section_legacy || "other",
      technology: anchor?.technology || "Not relevant",
      class: "Technical",
      text: "",
      is_requirement: true,
      sequence: null,
      flags: [],
      issues: [],
      review_status: "approved",
      se_comment: "",
      _isNew: true,
    };
    setRowsWithHistory((prev) =>
      anchorId ? insertRowAfter(prev, anchorId, newRow) : resequenceRows([...prev, newRow])
    );
    setSelected(new Set([tempId]));
    lastClicked.current = tempId;
  };

  const applyBulkTech = () => {
    if (!bulkTech || !selected.size) return;
    setRowsWithHistory((prev) => prev.map((r) => (selected.has(r.id) ? { ...r, technology: bulkTech } : r)));
    onToast?.info(`Product set on ${selected.size} row(s).`);
  };

  const toggleSection = (key) => {
    setCollapsedSections((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const onDragStart = (e, id) => {
    setDragId(id);
    e.dataTransfer.setData(ROW_DRAG_TYPE, id);
    e.dataTransfer.effectAllowed = "move";
  };

  const onRowDragOver = (e, targetId) => {
    e.preventDefault();
    e.stopPropagation();
    const rect = e.currentTarget.getBoundingClientRect();
    setDropHint({ targetId, insertAfter: e.clientY > rect.top + rect.height / 2 });
  };

  const onRowDrop = (e, targetId) => {
    e.preventDefault();
    e.stopPropagation();
    const sourceId = e.dataTransfer.getData(ROW_DRAG_TYPE) || dragId;
    if (!sourceId || sourceId === targetId) return;
    const insertAfter =
      dropHint?.targetId === targetId
        ? dropHint.insertAfter
        : e.clientY > e.currentTarget.getBoundingClientRect().top + e.currentTarget.getBoundingClientRect().height / 2;
    setRowsWithHistory((prev) => moveRowRelative(prev, sourceId, targetId, insertAfter));
    setDropHint(null);
    setDragId(null);
  };

  const handleDiscard = () => {
    if (!window.confirm("Discard all unsaved changes?")) return;
    skipHistoryRef.current = true;
    setRows(JSON.parse(JSON.stringify(baseline)));
    setSelected(new Set());
    skipHistoryRef.current = false;
    clearHistory();
    onToast?.info("Changes discarded.");
  };

  const inputCls =
    "rounded-lg border border-slate-300 bg-white px-2 py-1.5 text-sm dark:border-slate-600 dark:bg-slate-900";

  const productDecisionCount = useMemo(
    () => rows.filter((row) => openItemsCoveringRow(row, scopeResult).length).length,
    [rows, scopeResult]
  );
  useEffect(() => {
    if (decisionFocusId) setReviewFilter("product_decision");
  }, [decisionFocusId]);
  useEffect(() => {
    if (productDecisionFocus) setReviewFilter("product_decision");
  }, [productDecisionFocus]);
  useEffect(() => {
    if (loading) return;
    onOpenProductRowCount?.(productDecisionCount);
  }, [loading, productDecisionCount, onOpenProductRowCount]);
  useEffect(() => {
    if (loading || reviewFilter !== "product_decision" || decisionFocusId || productDecisionCount > 0) return;
    setReviewFilter("");
  }, [loading, reviewFilter, decisionFocusId, productDecisionCount]);

  const reviewChips = [
    { id: "", label: "All" },
    { id: "has_comment", label: "Has SE comment" },
    { id: "not_relevant", label: "Not relevant" },
    { id: "classification_review", label: "Classification review" },
    ...(productDecisionCount || decisionFocusId
      ? [{ id: "product_decision", label: productDecisionCount ? `Product decision (${productDecisionCount})` : "Product decision" }]
      : []),
    { id: "compliant", label: "Compliant" },
    { id: "partial", label: "Partial" },
    { id: "non_compliant", label: "Non-compliant" },
    { id: "error", label: "Error" },
  ];
  const hiddenTocCount = useMemo(
    () => (showToc ? 0 : hiddenTocIds.size),
    [hiddenTocIds, showToc]
  );
  const hiddenFocusCount = useMemo(
    () => (showExcluded ? 0 : hiddenFocusIds.size),
    [hiddenFocusIds, showExcluded]
  );

  const renderRow = (row) => {
    const isDropTarget = dropHint?.targetId === row.id;
    const dropCls = isDropTarget
      ? dropHint.insertAfter
        ? "border-b-2 border-sky-500"
        : "border-t-2 border-sky-500"
      : "";
    const hasComment = !!(row.se_comment || "").trim();
    const isPanel = panelRow?.id === row.id;
    const isFlashing = flashRowId === row.id;

    return (
      <tr
        key={row.id}
        data-row-id={row.id}
        className={`group/row border-t border-slate-200 align-top dark:border-slate-800 ${
          selected.has(row.id)
            ? "bg-sky-100/90 ring-1 ring-inset ring-sky-400/70 dark:bg-sky-950/35 dark:ring-sky-500/50"
            : hasComment
              ? "bg-amber-50/60 dark:bg-amber-950/25"
              : ""
        } ${isPanel ? "ring-2 ring-inset ring-emerald-400/70" : ""} ${isFlashing ? "bg-yellow-100 ring-2 ring-yellow-400 dark:bg-yellow-950/40 dark:ring-yellow-500" : ""} ${dragId === row.id ? "opacity-50" : ""} ${dropCls}`}
        onDragOver={(e) => onRowDragOver(e, row.id)}
        onDrop={(e) => onRowDrop(e, row.id)}
        onDragLeave={() => setDropHint((h) => (h?.targetId === row.id ? null : h))}
        onClick={() => setPanelRow(row)}
      >
        <td className="px-1 py-1" onClick={(e) => e.stopPropagation()}>
          <div
            draggable
            className="cursor-grab select-none text-center text-slate-400 active:cursor-grabbing"
            onDragStart={(e) => onDragStart(e, row.id)}
            onDragEnd={() => {
              setDragId(null);
              setDropHint(null);
            }}
          >
            ⋮⋮
          </div>
        </td>
        <td className="px-1 py-1" onClick={(e) => e.stopPropagation()}>
          <input
            type="checkbox"
            checked={selected.has(row.id)}
            onChange={() => {}}
            onClick={(e) => toggleSelect(row.id, e.shiftKey)}
            aria-label={`Select ${row.id}`}
          />
        </td>
        <td className="px-1 py-1 text-xs text-slate-500">{row.sequence ?? "—"}</td>
        <td className="px-1 py-1" onClick={(e) => e.stopPropagation()}>
          <div className="space-y-1">
            <select
              className={`w-full max-w-[8.5rem] rounded-lg border px-2 py-1.5 text-xs font-medium ${productChipClass(
                technologies.includes(row.technology) ? row.technology : row.technology || "Not relevant"
              )}`}
              value={technologies.includes(row.technology) ? row.technology : row.technology || "Not relevant"}
              onChange={(e) => updateRow(row.id, { technology: e.target.value })}
            >
              {!technologies.includes(row.technology) && row.technology ? (
                <option value={row.technology}>{row.technology}</option>
              ) : null}
              {technologies.map((t) => (
                <option key={t} value={t}>
                  {t}
                </option>
              ))}
            </select>
            {row.solution_type ? (
              <span className="block rounded bg-slate-100 px-1.5 py-0.5 text-[10px] font-medium text-slate-600 dark:bg-slate-800 dark:text-slate-300">
                {solutionLabel(row.solution_type)}
              </span>
            ) : null}
            {row.review_required ? (
              <span
                className="block rounded bg-amber-100 px-1.5 py-0.5 text-[10px] font-semibold text-amber-800 dark:bg-amber-950/50 dark:text-amber-200"
                title={(row.classification_warnings || []).join(" ") || row.classification_reason || "Classification requires review"}
              >
                ⚠ Ownership unclear
              </span>
            ) : null}
            {openItemsCoveringRow(row, scopeResult).map((item) => (
              <button
                key={item.item_id}
                type="button"
                className="block w-full rounded bg-amber-100 px-1.5 py-0.5 text-left text-[10px] font-semibold text-amber-900 dark:bg-amber-950/50 dark:text-amber-100"
                title={decisionItemTitle(item)}
                data-testid={`rfp-needs-product-decision-${row.id}`}
                onClick={() => {
                  if (dirty) {
                    onToast?.error("Save the grid before applying a product decision.");
                    return;
                  }
                  setDecisionDialog({ item, initial: null });
                }}
              >
                Needs product decision
              </button>
            ))}
            {resolvedDecisionForRow(row, scopeResult) ? (
              <span
                className="block rounded bg-slate-100 px-1.5 py-0.5 text-[10px] font-medium text-slate-600 dark:bg-slate-800 dark:text-slate-300"
                title={decisionItemTitle(resolvedDecisionForRow(row, scopeResult))}
              >
                {decisionLabel(resolvedDecisionForRow(row, scopeResult).product_decision, row)}
              </span>
            ) : null}
          </div>
        </td>
        <td className="px-1 py-1" onClick={(e) => e.stopPropagation()}>
          <select
            className={`w-full max-w-[6.5rem] rounded-lg border px-2 py-1.5 text-xs font-medium ${classSelectClass(
              row.class
            )}`}
            value={normalizeGridClass(row.class)}
            onChange={(e) => {
              const nextClass = e.target.value;
              updateRow(row.id, {
                class: nextClass,
                is_requirement: isRequirementFromClass(nextClass),
              });
            }}
          >
            {CLASSES.map((c) => (
              <option key={c} value={c}>
                {classLabel(c)}
              </option>
            ))}
          </select>
        </td>
        <td className="px-1 py-1" onClick={(e) => e.stopPropagation()}>
          {(() => {
            const compliance = complianceById?.[row.id];
            const fallbackLabel = fallbackDisagreementLabel(compliance?.chip, compliance?.fallback_action);
            return (
              <div className="flex flex-col items-start gap-0.5">
                <RfpComplianceChip
                  chip={compliance?.chip || ""}
                  skipped={row.is_requirement === false}
                  onClick={() => onOpenCompliance?.(row)}
                />
                {fallbackLabel ? (
                  <span
                    className="text-[11px] font-medium leading-tight text-slate-500 dark:text-slate-400"
                    data-testid="rfp-compliance-fallback-note"
                  >
                    Fallback: {fallbackLabel}
                  </span>
                ) : null}
              </div>
            );
          })()}
        </td>
        <td className="px-1 py-1" onClick={(e) => e.stopPropagation()}>
          <textarea
            className="min-h-[4rem] w-full rounded border border-slate-300 bg-white px-2 py-1 text-sm dark:border-slate-700 dark:bg-slate-900"
            value={row.text || ""}
            onChange={(e) => updateRow(row.id, { text: e.target.value })}
          />
        </td>
        <td className="px-1 py-1" onClick={(e) => e.stopPropagation()}>
          <textarea
            className={`min-h-[4rem] w-full rounded border px-2 py-1 text-sm ${
              hasComment
                ? "border-amber-400 bg-amber-50/80 dark:border-amber-700 dark:bg-amber-950/40"
                : "border-slate-300 bg-white dark:border-slate-700 dark:bg-slate-900"
            }`}
            placeholder="SE notes…"
            value={row.se_comment || ""}
            onChange={(e) => updateRow(row.id, { se_comment: e.target.value }, { autosaveComment: true })}
          />
        </td>
        <td className="px-1 py-1 text-center" onClick={(e) => e.stopPropagation()}>
          <button
            type="button"
            title="Delete row"
            aria-label="Delete row"
            onClick={(e) => deleteRow(row.id, e)}
            className="rounded p-1 text-slate-400 opacity-0 transition hover:bg-red-50 hover:text-red-600 group-hover/row:opacity-100 dark:hover:bg-red-950/40 dark:hover:text-red-400"
          >
            <TrashIcon />
          </button>
        </td>
      </tr>
    );
  };

  if (loading) {
    return (
      <div className="flex items-center gap-3 py-8 text-sm text-slate-500">
        <span className="inline-block h-5 w-5 animate-spin rounded-full border-2 border-emerald-600 border-t-transparent" />
        Loading requirements…
      </div>
    );
  }

  const btnCls =
    "rounded-lg border border-slate-300 bg-white px-2.5 py-1.5 text-sm text-slate-700 hover:bg-slate-50 dark:border-slate-600 dark:bg-slate-900 dark:text-slate-200 dark:hover:bg-slate-800 disabled:opacity-50";
  const btnPrimaryCls =
    "rounded-lg bg-emerald-700 px-3 py-1.5 text-sm font-medium text-white hover:bg-emerald-800 disabled:opacity-50 dark:bg-emerald-600 dark:hover:bg-emerald-500";

  return (
    <div className="space-y-2">
      {decisionFocusId ? (
        <p className="flex flex-wrap items-center gap-2 text-sm text-amber-900 dark:text-amber-100" data-testid="rfp-decision-focus">
          Showing rows for {decisionItemTitle(findDecisionItem(scopeResult, decisionFocusId) || {})}.
          <button type="button" className="text-xs underline" onClick={() => onClearDecisionFocus?.()}>
            Show all rows
          </button>
        </p>
      ) : null}
      {classificationSource ? (
        <p className="text-xs text-slate-500" data-testid="rfp-grid-classification-source">
          Classification source:{" "}
          {classificationSource === "scope_discovery_analysis"
            ? "RFP Discovery Scope Analysis"
            : "Analysis"}
        </p>
      ) : null}
      <div className="sticky top-0 z-30 -mx-1 space-y-2 rounded-lg border border-slate-200 bg-white/95 p-2.5 shadow-sm backdrop-blur dark:border-slate-700 dark:bg-slate-950/95">
        <RfpReviewFilterRow
          search={search}
          onSearchChange={setSearch}
          techFilter={techFilter}
          onTechFilterChange={setTechFilter}
          technologies={technologies}
          classFilter={classFilter}
          onClassFilterChange={setClassFilter}
          classes={CLASSES}
          classLabel={classLabel}
          reviewFilter={reviewFilter}
          onReviewFilterChange={(id) => {
            setReviewFilter(id);
            if (decisionFocusId) onClearDecisionFocus?.();
          }}
          reviewChips={reviewChips}
          showToc={showToc}
          onShowTocChange={setShowToc}
          hiddenTocCount={hiddenTocCount}
          showExcluded={showExcluded}
          onShowExcludedChange={setShowExcluded}
          hiddenFocusCount={hiddenFocusCount}
          autosaving={autosaving}
        />

        <div className="flex flex-wrap items-center gap-1.5">
          <select
            className={inputCls}
            value={bulkTech}
            onChange={(e) => setBulkTech(e.target.value)}
            aria-label="Set product"
          >
            <option value="">Set product…</option>
            {technologies.map((t) => (
              <option key={t} value={t}>
                {t}
              </option>
            ))}
          </select>
          <button type="button" disabled={!bulkTech || !selected.size} className={btnCls} onClick={applyBulkTech}>
            Apply product
          </button>
          <button type="button" className={btnCls} onClick={handleAddRow}>
            Add row
          </button>
          <button type="button" disabled={!canMergeSelected} className={btnCls} onClick={mergeSelected}>
            Merge rows
          </button>
          <button type="button" disabled={!selected.size} className={btnCls} onClick={deleteSelected}>
            Delete selected
          </button>
          <button type="button" disabled={!selected.size} className={btnCls} onClick={markNotRelevant}>
            Mark not relevant
          </button>
          <button
            type="button"
            className={btnCls}
            onClick={undo}
            disabled={!canUndo}
            title="Undo (Ctrl+Z)"
          >
            Undo
          </button>
          <button
            type="button"
            className={btnCls}
            onClick={redo}
            disabled={!canRedo}
            title="Redo (Ctrl+Y)"
          >
            Redo
          </button>
          <button type="button" disabled={!dirty || saving} className={`${btnPrimaryCls} ml-auto`} onClick={() => persistSave()}>
            {saving ? "Saving…" : "Save changes"}
          </button>
        </div>

        {selected.size > 0 && (
          <div className="flex flex-wrap items-center gap-2 rounded-lg border border-sky-300 bg-sky-50 px-3 py-2 dark:border-sky-700 dark:bg-sky-950/40">
            <span className="text-sm font-medium text-sky-900 dark:text-sky-100">
              {selected.size} row{selected.size !== 1 ? "s" : ""} selected
            </span>
            <button type="button" disabled={!bulkTech} className={btnCls} onClick={applyBulkTech}>
              Apply product
            </button>
            <button type="button" disabled={!canMergeSelected} className={btnCls} onClick={mergeSelected}>
              Merge rows
            </button>
            <button type="button" className={btnCls} onClick={markNotRelevant}>
              Mark not relevant
            </button>
            <button type="button" className={`${btnCls} text-red-700 dark:text-red-400`} onClick={deleteSelected}>
              Delete selected
            </button>
            <button type="button" className="text-xs text-slate-500 hover:underline" onClick={() => setSelected(new Set())}>
              Clear selection
            </button>
          </div>
        )}

        <div className="text-xs text-slate-500">
          {filtered.length} shown
          {dirty ? " · unsaved changes" : ""}
        </div>
      </div>

      {error && <p className="text-sm text-red-600 dark:text-red-400">{String(error)}</p>}
      {decisionDialog ? (
        <RfpProductDecisionDialog
          jobId={jobId}
          item={decisionDialog.item}
          initial={decisionDialog.initial}
          onClose={() => setDecisionDialog(null)}
          onApplied={(data) => {
            const updates = new Map((data?.updated_rows || []).map((row) => [row.id, row]));
            const merge = (list) =>
              list.map((row) => {
                const next = updates.get(row.id);
                if (!next) return row;
                const merged = {
                  ...row,
                  technology: next.technology,
                  label_source: next.label_source,
                };
                if (next.scope_decision_item_id) merged.scope_decision_item_id = next.scope_decision_item_id;
                else delete merged.scope_decision_item_id;
                return merged;
              });
            skipHistoryRef.current = true;
            setRows(merge);
            setBaseline(merge);
            skipHistoryRef.current = false;
            onProductDecisionApplied?.(data);
          }}
        />
      ) : null}

      <div className="flex flex-col lg:flex-row lg:gap-0">
        <div
          ref={scrollContainerRef}
          className="min-w-0 flex-1 overflow-x-auto overflow-y-auto rounded-xl border border-slate-200 dark:border-slate-800"
          style={{ maxHeight: "min(70vh, 720px)" }}
        >
          <table className="w-full table-fixed text-left text-sm">
            <colgroup>
              <col className="w-8" />
              <col className="w-9" />
              <col className="w-10" />
              <col style={{ width: "8.5rem" }} />
              <col style={{ width: "6.5rem" }} />
              <col style={{ width: "8.5rem" }} />
              <col />
              <col style={{ width: "12rem" }} />
              <col className="w-10" />
            </colgroup>
            <thead className="sticky top-0 z-10 border-b border-slate-200 bg-slate-50 text-xs font-semibold uppercase tracking-wide text-slate-600 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-400">
              <tr>
                <th className="px-1 py-2.5" />
                <th className="px-1 py-2.5">
                  <input
                    type="checkbox"
                    checked={filtered.length > 0 && filtered.every((r) => selected.has(r.id))}
                    onChange={(e) => selectAllVisible(e.target.checked)}
                    aria-label="Select all visible"
                  />
                </th>
                <th className="px-1 py-2.5">#</th>
                <th className="px-1 py-2.5">Product</th>
                <th className="px-1 py-2.5">Class</th>
                <th className="px-1 py-2.5">Compliance</th>
                <th className="px-1 py-2.5">Requirement</th>
                <th className="px-1 py-2.5">SE Comment</th>
                <th className="px-1 py-2.5" aria-label="Delete" />
              </tr>
            </thead>
            <tbody>
              {sectionGroups.map((group) => {
                const collapsed = collapsedSections.has(group.key);
                return (
                  <React.Fragment key={group.key}>
                    <tr className="bg-slate-200/80 dark:bg-slate-800/80">
                      <td colSpan={9} className="px-2 py-2">
                        <button
                          type="button"
                          className="flex items-center gap-2 text-left text-xs font-semibold uppercase tracking-wide text-slate-700 dark:text-slate-200"
                          onClick={() => toggleSection(group.key)}
                        >
                          <span>{collapsed ? "▶" : "▼"}</span>
                          {group.title}
                          <span className="font-normal normal-case text-slate-500">({group.rows.length} rows)</span>
                        </button>
                      </td>
                    </tr>
                    {!collapsed && group.rows.map((row) => renderRow(row))}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
        {panelRow && !panelCollapsed && (
          <RfpPdfPanel
            jobId={jobId}
            row={panelRow}
            sourceFormat={sourceFormat}
            onClose={() => setPanelRow(null)}
            onCollapse={() => setPanelCollapsed(true)}
          />
        )}
        {panelRow && panelCollapsed && (
          <div className="hidden shrink-0 lg:flex lg:w-10 lg:flex-col lg:border-l lg:border-slate-200 dark:lg:border-slate-700">
            <button
              type="button"
              title="Show source context"
              className="flex h-full min-h-[8rem] items-center justify-center border-b border-slate-200 text-xs text-slate-500 hover:bg-slate-100 dark:border-slate-700 dark:hover:bg-slate-800"
              onClick={() => setPanelCollapsed(false)}
            >
              ◀
            </button>
          </div>
        )}
      </div>

      <RfpStickySaveBar
        dirty={dirty}
        dirtyCount={dirtyCount}
        saving={saving}
        onSave={() => persistSave()}
        onDiscard={handleDiscard}
      />
    </div>
  );
}
